export const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);
const BILLING_FIELD_NAMES = [
    'stripe_customer_id',
    'stripe_subscription_id',
    'subscription_status',
    'subscription_price_id',
    'subscription_current_period_end',
    'subscription_cancel_at_period_end',
    'subscription_cancel_at',
    'billing_exempt',
    'billing_exempt_reason',
    'created_at',
    'updated_at',
];
const APP_ORIGIN = 'https://kodeo.app';

export const MONTHLY_SUBSCRIPTION = {
    name: 'Kodeo Membership',
    priceDisplay: '$9.99/month',
    tagline: 'Full access to every lesson, roadmap, and future updates.',
};

export function hasActiveSubscription(subscriptionStatus) {
    return ACTIVE_SUBSCRIPTION_STATUSES.has(subscriptionStatus);
}

function getFutureCancellationDate(user) {
    if (!user?.subscription_cancel_at) return null;

    const parsed = new Date(user.subscription_cancel_at);
    if (Number.isNaN(parsed.getTime())) return null;

    return parsed.getTime() > Date.now() ? parsed : null;
}

export function userHasPaidAccess(user) {
    if (!user) return false;
    if (user.isGuest) return false;
    if (user.is_admin) return true;
    if (user.billing_exempt) return true;
    return hasActiveSubscription(user.subscription_status);
}

export function enrichUserAccess(user) {
    if (!user) return user;

    const billing = user.userBilling || user.billing || null;
    const billingFields = BILLING_FIELD_NAMES.reduce((fields, fieldName) => {
        if (billing?.[fieldName] !== undefined) {
            fields[fieldName] = billing[fieldName];
        } else if (user?.[fieldName] !== undefined) {
            fields[fieldName] = user[fieldName];
        }
        return fields;
    }, {});

    const subscriptionStatus = billing?.subscription_status ?? user.subscription_status ?? null;
    const isBillingExempt = Boolean(billing?.billing_exempt ?? user.billing_exempt);
    const mergedUser = {
        ...user,
        ...billingFields,
        subscription_status: subscriptionStatus,
        billing_exempt: isBillingExempt,
    };

    return {
        ...mergedUser,
        subscriptionStatus,
        isBillingExempt,
        hasPaidAccess: userHasPaidAccess({ ...mergedUser, subscription_status: subscriptionStatus, billing_exempt: isBillingExempt }),
    };
}

export function sanitizeInternalRedirectPath(redirectTo) {
    if (!redirectTo || typeof redirectTo !== 'string') return null;

    const trimmed = redirectTo.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
        return null;
    }

    try {
        const parsed = new URL(trimmed, APP_ORIGIN);
        if (parsed.origin !== APP_ORIGIN) {
            return null;
        }

        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
        return null;
    }
}

export function getSubscriptionRedirectPath(redirectTo) {
    const safeRedirectTo = sanitizeInternalRedirectPath(redirectTo);
    if (!safeRedirectTo) return '/subscription';
    return `/subscription?redirectTo=${encodeURIComponent(safeRedirectTo)}`;
}

export function getPostCheckoutDestination(user, redirectTo, fallbackPath) {
    const safeRedirectTo = sanitizeInternalRedirectPath(redirectTo);
    const safeFallbackPath = sanitizeInternalRedirectPath(fallbackPath) || '/lessons';

    if (!userHasPaidAccess(user)) {
        return getSubscriptionRedirectPath(safeRedirectTo);
    }

    return safeRedirectTo || safeFallbackPath;
}

export function getAccessDestination(user, redirectTo, fallbackPath = '/lessons') {
    return getPostCheckoutDestination(user, redirectTo, fallbackPath);
}

export function getSubscriptionStatusLabel(user) {
    if (!user || user.isGuest) return 'Guest preview';
    if (user.is_admin) return 'Admin access';
    if (user.billing_exempt) return 'Complimentary access';

    switch (user.subscription_status) {
        case 'active':
            if (user.subscription_cancel_at_period_end) return 'Active until period end';
            if (getFutureCancellationDate(user)) return 'Cancellation scheduled';
            return 'Active subscription';
        case 'trialing':
            if (user.subscription_cancel_at_period_end) return 'Trial ending at period end';
            if (getFutureCancellationDate(user)) return 'Trial with cancellation scheduled';
            return 'Trialing';
        case 'past_due':
            return 'Payment issue';
        case 'canceled':
            return 'Canceled';
        case 'incomplete':
            return 'Checkout incomplete';
        case 'incomplete_expired':
            return 'Checkout expired';
        case 'unpaid':
            return 'Unpaid';
        default:
            return 'Subscription required';
    }
}

function formatBillingDate(value) {
    if (!value) return null;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(parsed);
}

export function getAccessNote(user) {
    if (!user || user.is_admin || !user.billing_exempt_reason) return null;
    return user.billing_exempt_reason.replace(/_/g, ' ');
}

export function getSubscriptionTimingNote(user) {
    if (!user || user.isGuest || user.is_admin || user.billing_exempt) return null;

    if (user.subscription_cancel_at_period_end) {
        const cancelAtLabel = formatBillingDate(
            user.subscription_cancel_at || user.subscription_current_period_end
        );
        return cancelAtLabel
            ? `Your subscription is set to cancel at the end of the current billing period on ${cancelAtLabel}.`
            : 'Your subscription is set to cancel at the end of the current billing period.';
    }

    const futureCancellationDate = getFutureCancellationDate(user);
    if (futureCancellationDate) {
        const cancelAtLabel = formatBillingDate(futureCancellationDate.toISOString());
        return cancelAtLabel
            ? `Your subscription is scheduled to cancel on ${cancelAtLabel}.`
            : 'Your subscription is scheduled to cancel.';
    }

    if (user.subscription_status === 'canceled') {
        const endedAtLabel = formatBillingDate(user.subscription_cancel_at || user.subscription_current_period_end);
        return endedAtLabel
            ? `Your subscription ended on ${endedAtLabel}.`
            : 'Your subscription has been canceled.';
    }

    return null;
}

export async function fetchUserAccessState(supabase, authId) {
    const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, auth_id, is_admin')
        .eq('auth_id', authId)
        .single();

    if (userError || !userData) {
        return null;
    }

    const { data: billingData } = await supabase
        .from('user_billing')
        .select('billing_exempt, subscription_status')
        .eq('auth_id', authId)
        .maybeSingle();

    return enrichUserAccess({
        ...userData,
        ...(billingData || {}),
        userBilling: billingData || null,
    });
}
